/**
 * @lines98/client/lib/achievement-catalog — клиентская локаль-витрина каталога
 * ачивок (id → название + однозначное описание условия) для секции «Достижения».
 *
 * ЗАЧЕМ / WHY THIS EXISTS (achievements.md §6.3; owner/art): серверный
 * `ACHIEVEMENT_CATALOG` (@lines98/shared/config/meta) — это МЕХАНИКА (id / категория
 * / триггер / тиры / награда), БЕЗ названий и копи. Имена/описания — в локали-
 * каталогах (`content/i18n/<lang>.json`, ключи `game.ach.<id>.{name,desc}`), по
 * стабильному `id` (как требует spec §6.3 «UI берёт описания из локали»). Этот файл —
 * витрина, которая склеивает механику (shared-каталог) с локализованным текстом
 * (`t()`): badge-grid, деталь-попап и тост разблокировки берут имя/описание отсюда.
 * Поменять текст = поменять перевод в JSON, без касания механики.
 *
 * **РЕАКТИВНОСТЬ ЛОКАЛИ (i18n, ADR-0021):** `name`/`description` резолвятся через
 * `t('game.ach.<id>.name|desc')` в МОМЕНТ доступа (getter на записи Display), а НЕ
 * запекаются на import. Достижения рендерятся в profile / окне-результате / детали,
 * которые ремонтируются на смену языка → при чтении `def.name` берётся строка
 * АКТИВНОГО языка. RU-литералов имён/описаний в этом файле больше нет (всё через
 * каталоги i18n; fallback-цепочка `активный → en → ключ` — в `t()`).
 *
 * **Описания — КОНКРЕТНЫЕ, однозначные (owner-вход, spec §3 P-1):** «5 линий по 5+
 * за партию», не «будь молодцом». Каждое описание = проверяемое условие, к которому
 * игрок может идти осознанно. Размытые формулировки НЕ принимаются.
 *
 * Server-truth (I-3): сам факт разблокировки — серверный (`player_achievements`).
 * Этот файл НЕ решает, открыта ли ачивка — только КАК её назвать. Если сервер
 * прислал id, которого тут нет (новая ачивка опередила локаль) — graceful fallback
 * (имя = id), не падаем.
 *
 * **Скрытые-сюрпризы (spec §3 / AC-7):** 3 ачивки (`fun_buzzer_beater`/
 * `fun_full_board_loss`/`fun_comeback`) помечены `hidden` в shared-каталоге. До
 * разблокировки их критерий маскируется («???») — badge-grid решает по `hidden` +
 * `unlocked`. После разблокировки — раскрываются.
 *
 * SCOPE: склейка id → локализованные {name, description} (через `t()`); редкость/
 * категория/тиры берутся из shared-каталога (single source механики). NOT IN SCOPE:
 * триггер-логика (server-side achievements.ts), пороги (config), бейдж-арт (плейсхолдер,
 * art-assets), сами переводы (живут в `content/i18n/<lang>.json`).
 *
 * Cross-references:
 *   - @lines98/shared/config/meta `ACHIEVEMENT_CATALOG` (механика — id/категория/триггер).
 *   - content/i18n/<lang>.json `game.ach.<id>.{name,desc}` (локализованный текст — 15 языков).
 *   - lib/i18n `t()` (резолв ключа в строку активного языка; ADR-0021 D-1).
 *   - docs/specs/achievements.md §3 (каталог 28 + условия) / §6.3 (UI-источник).
 *   - components/badge-grid (потребитель) / components/achievement-detail (деталь-попап).
 */

import {
  ACHIEVEMENT_CATALOG,
  ACH_REWARD,
  type AchievementDef,
  type AchievementRarity,
} from '@lines98/shared/config/meta';

import { t } from './i18n/index.js';

/** Отображаемая запись ачивки (имя + однозначное описание условия) для UI. */
export interface AchievementDisplay {
  /** Стабильный id (= `ACHIEVEMENT_CATALOG[].id`). */
  readonly id: string;
  /** Короткое RU-название (label, must-visible — не обрезается). */
  readonly name: string;
  /** Описание «как открыть» — КОНКРЕТНОЕ условие (descriptive, за `?`/деталь). */
  readonly description: string;
  /** Класс редкости (определяет вид бейджа / акцент). От shared-каталога (single source). */
  readonly rarity: AchievementRarity;
  /** Число ступеней (под-тиров) этой ачивки. */
  readonly tierCount: number;
  /** Скрытая-сюрприз: до разблокировки маскируется «???» (badge-grid решает по `unlocked`). */
  readonly hidden: boolean;
}

/**
 * Локализованное имя ачивки по id. Резолвится в МОМЕНТ доступа (реактивность локали).
 * Fallback (`t()` отдаёт ключ-как-есть, если строка не заведена) → подменяем на id,
 * чтобы новая ачивка, опередившая локаль, показывала id, а не сырой ключ.
 */
export function achievementName(id: string): string {
  const key = `game.ach.${id}.name`;
  const text = t(key);
  return text === key ? id : text;
}

/**
 * Локализованное описание «как открыть» по id. Резолвится в МОМЕНТ доступа. Если
 * строка не заведена (`t()` вернул ключ) — generic-fallback `game.ach.default.desc`.
 */
export function achievementDesc(id: string): string {
  const key = `game.ach.${id}.desc`;
  const text = t(key);
  return text === key ? t('game.ach.default.desc') : text;
}

/**
 * Отображаемая запись для одной ачивки по её механ-определению. `name`/`description` —
 * GETTER'ы: резолвят `t('game.ach.<id>.*')` в МОМЕНТ чтения (реактивность локали),
 * НЕ запекают строку на import. Редкость/тиры/hidden — из shared-каталога (статичны).
 */
function toDisplay(def: AchievementDef): AchievementDisplay {
  return {
    id: def.id,
    get name() {
      return achievementName(def.id);
    },
    get description() {
      return achievementDesc(def.id);
    },
    rarity: def.rewardRarityByStep[0] ?? 'common',
    tierCount: def.tiers.length,
    hidden: def.hidden === true,
  };
}

/**
 * Полный отображаемый каталог ачивок (в порядке `ACHIEVEMENT_CATALOG`). badge-grid
 * рисует все: открытые подсвечены, закрытые — приглушённый плейсхолдер; скрытые
 * (`hidden`) до разблокировки маскируются «???».
 */
export const ACHIEVEMENT_DISPLAY: readonly AchievementDisplay[] = ACHIEVEMENT_CATALOG.map(toDisplay);

/** Быстрый lookup отображаемой записи по id (для попапа разблокировки). */
const BY_ID = new Map<string, AchievementDisplay>(ACHIEVEMENT_DISPLAY.map((d) => [d.id, d]));

/**
 * Отображаемая запись ачивки по id. На неизвестный id — синтетический fallback с
 * getter'ами (имя = id; описание = generic), чтобы попап разблокировки / грид не
 * падали (forward-compat) и при этом оставались локаль-реактивными.
 */
export function achievementDisplay(id: string): AchievementDisplay {
  return (
    BY_ID.get(id) ?? {
      id,
      get name() {
        return achievementName(id);
      },
      get description() {
        return achievementDesc(id);
      },
      rarity: 'common',
      tierCount: 1,
      hidden: false,
    }
  );
}

/** Сколько всего ачивок в каталоге (для счётчика «X из N»). */
export const ACHIEVEMENT_TOTAL = ACHIEVEMENT_DISPLAY.length;

/** Lookup механ-определения ачивки по id (для вывода награды из под-тира). */
const DEF_BY_ID = new Map<string, AchievementDef>(ACHIEVEMENT_CATALOG.map((d) => [d.id, d]));

/** Награда ачивки (золото/осколки), выведенная из каталога по `(id, tier_index)`. */
export interface AchievementReward {
  readonly gold: number;
  readonly shards: number;
}

/**
 * Награда за разблокировку `(id, tier_index)` — выводится ЛОКАЛЬНО из shared-каталога
 * (`rewardRarityByStep[tier_index]` → `ACH_REWARD[rarity]`), БЕЗ запроса к серверу.
 *
 * ЗАЧЕМ: экран добычи (T-RESULT) объясняет, ОТКУДА золото/осколки — «+N за ачивку X».
 * Сервер шлёт в сводке только `{ id, tier_index }` (без сумм) — этого ДОСТАТОЧНО:
 * награда детерминированно выводится из каталога (тот же источник правды, что у
 * сервера). Неизвестный id / out-of-range tier → `{0,0}` (forward-compat).
 *
 * @param id — `achievement_id` из сводки.
 * @param tierIndex — под-тир из сводки (0-based).
 * @returns награда `{ gold, shards }` этой ступени.
 */
export function achievementReward(id: string, tierIndex: number): AchievementReward {
  const def = DEF_BY_ID.get(id);
  const rarity = def?.rewardRarityByStep[tierIndex] ?? def?.rewardRarityByStep[0];
  if (!rarity) return { gold: 0, shards: 0 };
  const r = ACH_REWARD[rarity];
  return { gold: r.gold, shards: r.shards };
}
